import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid,  MenuItem, Radio, RadioGroup, Select, SelectChangeEvent, TextField, Typography, styled } from '@mui/material'
import React, { ChangeEvent, useContext, useState } from 'react'
import { Buffer } from 'buffer';
import ButtonGradient from '../Filter/Components/ButtonGradient';
import ButtonLightGray from '../Filter/Components/ButtonLightGray';
import { APIsContext } from '../../Store/APIs/context';
import { CreateSubjectFromScannerImagesRequest, CreateSubjectFromScannerImagesRequestFingerPosition, CreateSubjectRequest, CreateSubjectRequestFingerPosition, CreateSubjectRequestModality, CreateSubjectRequestOption, MultimodalSubject } from 'neurotec-megamatcherid-management-client';
import { useSnackbar } from 'notistack';
import DialogTitleClose from '../Misc/Dialog/Components/DialogTitleClose';
import { THEME } from '../../config';
import { Modality } from '../../Store/Operations/types';
import theme from '../../config/Theme';
import AudioOperationSummary from '../OverviewView/AudioOperationSummary';
import StyledTooltip from '../Misc/StyledTooltip';
import DialogTypographyTitle from '../Misc/Dialog/Components/DialogTypographyTitle';
import { handleCutStringHelper } from '../../helpers/displayStringHelpers';
import SelectCheckmarks from '../Filter/Components/SelectCheckmarks';
import { NavigationContext } from '../../Store/Navigation/context';
import { setLoading } from '../../Store/Navigation/reducer';
import { AxiosResponse } from 'axios';

const ACCEPT_IMAGE = "image/*"
const ACCEPT_AUDIO = "audio/*"

const SelectType = styled(Select)({
    padding: "none",
})

const RadioButton = styled(Radio)({
    color: THEME.palette.primary.main
})

const GridContainer = styled(Grid)(({theme}) => ({
    ".MuiGrid-item": {
        padding: "0px",
        paddingLeft: "0.5rem",
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "0px"
        }
    }
}));

const GridInfo = styled(Grid)(({theme}) => ({
    display: "flex",
    flexDirection: "column",
    gap: 1,
    paddingTop: "0px",
    [theme.breakpoints.down("md")]: {
        paddingLeft: "2rem"
    }
}));

const GridFile = styled(Grid)(({theme}) => ({
    [theme.breakpoints.down("md")]: {
        padding: theme.spacing(2)
    },
}))

const BoxFile = styled(Box)(() => ({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    borderStyle: "solid",
    borderWidth: "2px",
    borderColor: "#d7d7d7",
    borderRadius: "6px",
    overflow: "hidden", 
    textOverflow: "ellipsis", 
    whiteSpace: "nowrap",
    height: "calc(100% - 4px)",
    paddingLeft: "2px",
    [theme.breakpoints.down("sm")]: {
        paddingTop: "2px",
        marginTop: "0.5rem"
    },
    [theme.breakpoints.down("md")]: {
        marginTop: "0.5rem"
    },
}))

const readFile = async (file: File): Promise<Uint8Array> => {
    return new Promise<Uint8Array>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            let result = new Uint8Array(reader.result as ArrayBuffer);
            resolve(result);
        }
        reader.onerror = (event) => {
            reject(reader.error)
        }
        reader.readAsArrayBuffer(file);
    })
}
interface IAddSubjectDialogProps {
    open: boolean,
    onClose: () => void
    rerender: () => void
}

const AddSubjectDialog: React.FC<IAddSubjectDialogProps> = (props) => {

    const { enqueueSnackbar } = useSnackbar();
    const navigationDispatch = useContext(NavigationContext).dispatch
    const [fileType, setFileType] = useState<CreateSubjectRequestOption>(CreateSubjectRequestOption.Image)
    const [image, setImage] = useState("");
    const [audio, setAudio] = useState("");
    const [fileName, setFileName] = useState("")
    const [file, setFile] = useState("")
    const [fingerPosition, setFingerPosition] = useState<CreateSubjectRequestFingerPosition | CreateSubjectFromScannerImagesRequestFingerPosition | undefined>(undefined)
    const [missingFingerPosition, setMissingFingerPosition] = useState<CreateSubjectRequestFingerPosition[] | undefined>(undefined)
    const [detectLiveness, setDetectLiveness] = useState(false);
    const [checkIcao, setCheckIcao] = useState(false)
    const [modality, setModality] = useState<CreateSubjectRequestModality>(CreateSubjectRequestModality.FaceModality)
    const [subjectIdError, setSubjectIdError] = useState(false)
    const { state } = useContext(APIsContext)
    
    const handleOnClose = () => {
        props.onClose()
    }

    const handleExit = () => {
        setFileType(CreateSubjectRequestOption.Image)
        setImage("");
        setAudio("")
        setFileName("")
        setFile("")
        setDetectLiveness(false)
        setCheckIcao(false)
        setSubjectIdError(false)
        setModality(Modality.FACE_MODALITY)
        setFingerPosition(undefined)
        setMissingFingerPosition([])
    }

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const subjectId = new FormData(event.currentTarget).get("subject-id") as string;
        if ((subjectId === null || subjectId === "")) {
            enqueueSnackbar("Subject Id must not be blank", {variant: "warning"})
            setSubjectIdError(true)
            return
        }
        setSubjectIdError(false)
        if (file === null || file === "") {
            enqueueSnackbar("No file selected", {variant: "warning"})
            return
        }

        navigationDispatch(setLoading(true))
        if (fileType === CreateSubjectRequestOption.ScannerImage) {
            handleCreateSubjectFromScanner(subjectId)
        } else {
            handleCreateSubject(subjectId)
        }
        handleOnClose()
    }

    const handleRequest = (res: AxiosResponse<MultimodalSubject>) => {
        navigationDispatch(setLoading(false))
        if (res.status === 200) {
            if (res.data && res.data.multimodalOperations && res.data.multimodalOperations.length !== 0 && res.data.multimodalOperations[0].status) {
                switch (res.data.multimodalOperations[0].status) {
                    case 'SUCCESS':
                        enqueueSnackbar("Subject created successfully", { variant: 'info' });
                        props.rerender();
                        break;
                    case "FAILED":
                        enqueueSnackbar("Subject was not created, failed ICAO check", { variant: 'warning' });
                        break;
                    case "LIVENESS_CHECK_FAILED":
                        enqueueSnackbar("Subject was not created, failed liveness check", { variant: 'warning' });
                        break;
                    default:
                        enqueueSnackbar("Subject was not created, status: " + res.data.multimodalOperations[0].status, { variant: 'warning' });
                        break; 
                }
            } else {
                enqueueSnackbar("Failed to create subject", { variant: 'error' });
            }
        } else {
            enqueueSnackbar("Failed to create subject", { variant: 'error' });
        }
    }

    const handleCreateSubject = (subjectId: string) => {
        const request: CreateSubjectRequest = {
            detectLiveness: detectLiveness,
            checkIcaoCompliance: checkIcao,
            subjectId: subjectId,
            data: file,
            option: fileType,
            modality: modality,
            fingerPosition: fingerPosition,
            missingFingerPosition: missingFingerPosition
        }

        state.SubjectsApi.createSubject({createSubjectRequest: request}).then(res => {
            handleRequest(res)
        }).catch(err => {
            navigationDispatch(setLoading(false))
            enqueueSnackbar(err.message, { variant: 'error' });
        })
    }

    const handleCreateSubjectFromScanner = (subjectId: string) => {
        const request: CreateSubjectFromScannerImagesRequest = {
            subjectId: subjectId,
            data: [file],
            fingerPosition: [fingerPosition ?? "UNRECOGNIZED"],
            missingFingerPosition: missingFingerPosition
        }
        
        state.SubjectsApi.createSubjectFromScannerImages({createSubjectFromScannerImagesRequest: request}).then(res => {
            handleRequest(res)
        }).catch(err => {
            navigationDispatch(setLoading(false))
            enqueueSnackbar(err.message, { variant: 'error' });
        })
    }

    const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) {
            return;
        }
        const file = e.target.files[0];
        setFileName(file.name)
        const fileContent = await readFile(file)
        setFile(Buffer.from(fileContent).toString('base64'));
        if (fileType === CreateSubjectRequestOption.Image || fileType === CreateSubjectRequestOption.ScannerImage) {
            setImage(URL.createObjectURL(file))
        }
        if (fileType === CreateSubjectRequestOption.Audio) {
            setAudio(Buffer.from(fileContent).toString('base64'))
        }
    }

    const handleFileTypeChange = (event: SelectChangeEvent<unknown>) => {
        setFileType(event.target.value as CreateSubjectRequestOption)
    }

    const handleFingerPosition = (event: SelectChangeEvent<unknown>) => {
        setFingerPosition(event.target.value === "" ? undefined : event.target.value as CreateSubjectRequestFingerPosition);
    }

    const handleFingerMissingPosition = (value: any[]) => {
        setMissingFingerPosition(value)
    }

    const handleModalityChange = (event: SelectChangeEvent<unknown>) => {
        setModality(event.target.value as CreateSubjectRequestModality)
        if (event.target.value as CreateSubjectRequestModality === Modality.FACE_MODALITY) {
            setImage("")
            setAudio("")
            setFileName("")
            setFile("")
            if (fileType === CreateSubjectRequestOption.Audio || fileType === CreateSubjectRequestOption.ScannerImage) {
                setFileType(CreateSubjectRequestOption.Image)
            }
        }
        if (event.target.value as CreateSubjectRequestModality === Modality.VOICE_MODALITY) {
            setCheckIcao(false)
            setDetectLiveness(false)
            setImage("")
            setAudio("")
            setFileName("")
            setFile("")
            if (fileType === CreateSubjectRequestOption.Image || fileType === CreateSubjectRequestOption.ScannerImage) {
                setFileType(CreateSubjectRequestOption.Audio)
            }
        }
        if (event.target.value as CreateSubjectRequestModality === Modality.FINGERS_MODALITY) {
            setCheckIcao(false)
            setDetectLiveness(false)
            setImage("")
            setAudio("")
            setFileName("")
            setFile("")
            if (fileType === CreateSubjectRequestOption.Audio) {
                setFileType(CreateSubjectRequestOption.Image)
            }
        }
    }

    const handleMissingPositionType = (): string[] => {
        let list: string[] = []
        Object.values(CreateSubjectRequestFingerPosition).forEach((e) => {
            switch (e) {
                case CreateSubjectRequestFingerPosition.FingerPositionLeftIndex:
                case CreateSubjectRequestFingerPosition.FingerPositionLeftLittle:
                case CreateSubjectRequestFingerPosition.FingerPositionLeftMiddle:
                case CreateSubjectRequestFingerPosition.FingerPositionLeftRing:
                case CreateSubjectRequestFingerPosition.FingerPositionLeftThumb:
                case CreateSubjectRequestFingerPosition.FingerPositionRightIndex:
                case CreateSubjectRequestFingerPosition.FingerPositionRightLittle:
                case CreateSubjectRequestFingerPosition.FingerPositionRightMiddle:
                case CreateSubjectRequestFingerPosition.FingerPositionRightRing:
                case CreateSubjectRequestFingerPosition.FingerPositionRightThumb:
                    list.push(e)
            }
        })
        return list
    }

    const handleFilePreview = () => {
        if (fileType === CreateSubjectRequestOption.Image || fileType === CreateSubjectRequestOption.Audio || fileType === CreateSubjectRequestOption.ScannerImage) {
            switch (modality) {
                case Modality.FACE_MODALITY:
                    return  image ?
                        <img src={image} style={{ maxHeight: "250px", maxWidth: "100%" }} alt="" />
                        : (<Typography style={{lineHeight: "40px"}}>Image preview</Typography>)
                case Modality.VOICE_MODALITY:
                    return audio ?
                        <AudioOperationSummary audioInfo={{audio: audio}}/>
                        : (<Typography style={{lineHeight: "40px"}}>Audio preview</Typography>)
                case Modality.FINGERS_MODALITY:
                    return image ?
                        <img src={image} style={{ maxHeight: "250px", maxWidth: "100%" }} alt="" />
                        : (<Typography style={{lineHeight: "40px"}}>Image preview</Typography>)
            }
        } else {
            return (<Typography style={{lineHeight: "40px"}}>No preview available</Typography>)
        }
    }

    const handleOption = () => {
        switch (modality) {
            case Modality.FACE_MODALITY:
                return (
                    <>
                        <Grid item xs={12}>
                            <DialogTypographyTitle>Options</DialogTypographyTitle>
                        </Grid>
                        <Grid container>
                            <Grid item xs={6} style={{paddingTop: "0px"}}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={detectLiveness}
                                            onChange={() => setDetectLiveness(!detectLiveness)}
                                        />
                                    }
                                    label="Detect liveness"
                                    disabled={modality === Modality.FACE_MODALITY ? false : true}
                                />
                            </Grid>
                            <Grid item xs={6} style={{paddingTop: "0px"}}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={checkIcao}
                                            onChange={() => setCheckIcao(!checkIcao)}
                                        />
                                    }
                                    label="Check Icao"
                                    disabled={modality === Modality.FACE_MODALITY ? false : true}
                                />
                            </Grid>
                        </Grid>
                    </>
                )
            case Modality.VOICE_MODALITY:
                return (
                    <Grid container></Grid>
                )
            case Modality.FINGERS_MODALITY:
                return (
                    <>
                        <Grid item xs={12}>
                            <DialogTypographyTitle>Finger position</DialogTypographyTitle>
                        </Grid>
                        <Grid item xs={12}>
                            <SelectType
                                value={fingerPosition ?? ""}
                                onChange={handleFingerPosition}
                                style={{ padding: "0px", marginBottom: "0.25rem", width: "100%" }}
                                MenuProps={{ MenuListProps: { disablePadding: true }}}
                            >
                                <MenuItem value="">None</MenuItem>

                                {Object.values(CreateSubjectRequestFingerPosition).map(element => (
                                    element !== CreateSubjectRequestFingerPosition.Unrecognized?
                                    <MenuItem key={element} value={element}>
                                        {handleCutStringHelper("FINGER_POSITION_", element)}
                                    </MenuItem> : null
                                ))}
                            </SelectType>
                        </Grid>
                        <Grid item xs={12}>
                            <DialogTypographyTitle>Finger missing position</DialogTypographyTitle>
                        </Grid>
                        <Grid item xs={12} style={{paddingLeft: 0, marginBottom: "1rem"}}>
                            <SelectCheckmarks
                                value={missingFingerPosition}
                                setValue={handleFingerMissingPosition}
                                type={handleMissingPositionType()}
                                source={"STRING"}
                                prefix='FINGER_POSITION_'
                            />
                        </Grid>
                    </>
                )
            default:
                break;
        }
    }

    return (
        <Dialog
            maxWidth="md"
            scroll="body"
            fullWidth
            open={props.open}
            onClose={handleOnClose}
            onTransitionExited={handleExit}
        >
            <Box component="form" onSubmit={handleSubmit} noValidate >
                <DialogTitle>
                    <DialogTitleClose title={"Add subject"} onClose={handleOnClose}/>
                </DialogTitle>
                <DialogContent>
                    <GridContainer container>
                        <Grid item xs={12} sm={6} style={{ backgroundColor: "#E8E8E8", display: "flex", justifyContent: "center", alignItems: "center", paddingLeft: "0px" }}>
                            {handleFilePreview()}
                        </Grid>
                        <GridInfo item xs={12} sm={6} style={{paddingTop: "0.5rem"}}>
                            <TextField
                                sx={{margin: "none"}}
                                style={{paddingBottom: "0.5rem"}}
                                autoComplete="off"
                                id="subject-id"
                                name="subject-id"
                                label="Subject ID"
                                inputProps={{
                                    style: {paddingBottom: "0.5rem"}
                                }}
                                InputLabelProps={{
                                    sx: {top: "-7px", "&.MuiInputLabel-shrink": {top: "0px"}},
                                }}
                                error={subjectIdError}
                            />
                            <SelectType
                                value={fileType}
                                onChange={handleFileTypeChange}
                                style={{padding: "0px", marginBottom: "0.25rem"}}
                                MenuProps={{MenuListProps: {disablePadding: true}}}
                            >
                                <MenuItem disabled={modality !== Modality.FACE_MODALITY && modality !== Modality.FINGERS_MODALITY} value={CreateSubjectRequestOption.Image}>Image file</MenuItem>
                                <MenuItem disabled={!(modality === Modality.FINGERS_MODALITY)} value={CreateSubjectRequestOption.ScannerImage}>Scanner image file</MenuItem>
                                <MenuItem disabled={modality !== Modality.VOICE_MODALITY} value={CreateSubjectRequestOption.Audio}>Audio file</MenuItem>
                                <MenuItem value={CreateSubjectRequestOption.NTemplate}>NTemplate</MenuItem>
                                <MenuItem value={CreateSubjectRequestOption.MmidTemplate}>MMID Template</MenuItem>
                            </SelectType>
                            <Grid container>
                                <Grid item xs={12}>
                                    <FormControl>
                                        <DialogTypographyTitle>Modality</DialogTypographyTitle>
                                        <RadioGroup
                                            row
                                            value={modality}
                                            onChange={handleModalityChange}
                                        >
                                            <FormControlLabel value={CreateSubjectRequestModality.FaceModality} control={ <RadioButton/>} label="Face" color="primary" />
                                            <FormControlLabel value={CreateSubjectRequestModality.VoiceModality} control={<RadioButton />} label="Voice" color="primary" />
                                            <FormControlLabel value={CreateSubjectRequestModality.FingersModality} control={<RadioButton />} label="Fingers" color="primary" />
                                        </RadioGroup>
                                    </FormControl>
                                </Grid>
                                {handleOption()}
                            </Grid>
                            <Grid container>
                                <Grid item xs={12} md={4}>     
                                    <Button variant="outlined" component="label" style={{width: "100%", height: "100%"}}>
                                        Choose file
                                        <input
                                            hidden
                                            type='file'
                                            accept={(modality === Modality.FACE_MODALITY  || modality === Modality.FINGERS_MODALITY) ? ACCEPT_IMAGE : ACCEPT_AUDIO}
                                            onChange={handleFileUpload}
                                        />
                                    </Button>
                                </Grid>
                                <GridFile item xs={12} md={8}>
                                    {fileName !== "" ?
                                        <StyledTooltip title={fileName}>
                                            <BoxFile>
                                                {fileName}
                                            </BoxFile>
                                        </StyledTooltip> : null
                                    }
                                </GridFile>
                            </Grid>
                        </GridInfo>
                    </GridContainer>
                </DialogContent>
                <DialogActions>
                    <ButtonLightGray onClick={handleOnClose}>
                        Cancel
                    </ButtonLightGray>
                    <ButtonGradient variant="contained" color="primary" type='submit'>
                        Create subject
                    </ButtonGradient>
                </DialogActions>
            </Box>
        </Dialog >
    )
}

export default AddSubjectDialog
